import {
  type AgentChatBranding,
  AgentProvider,
  type OnAgentEvent,
} from '@kapaai/agent-react';
import { Box } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { KapaBetaSignupLink } from 'components/agent/kapa-beta-signup-link';
import { useAuth } from 'lib/auth';
import { PRIMARY_COLOR } from 'lib/theme';
import posthog from 'posthog-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  AgentUIProvider,
  useAgent,
} from '../components/agent/agent-panel-wrapper';
import {
  AGENT_API_BASE_URL,
  AGENT_INTEGRATION_ID,
  AGENT_MODEL,
  AGENT_PROJECT_ID,
} from './agent/constants';
import { SourceHighlightsContext } from './agent/source-highlights';
import {
  AGENT_CUSTOM_INSTRUCTIONS,
  BUILTIN_TOOL_META,
  createAgentTools,
  type ToolContext,
} from './agent/tools';
import { type SourceChange } from './agent/webcrawl-setup-tools';

const KAPA_EXAMPLE_PROMPTS = [
  'What are the top questions last week?',
  'How has my self-serve rate trended this year?',
  'Help me add a new web crawl as a source',
];

const ProjectResetHandler = ({ projectId }: { projectId: string }) => {
  const { resetConversation } = useAgent();

  useEffect(() => {
    resetConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return null;
};

export const KAPA_BRANDING: AgentChatBranding = {
  title: (
    <>
      Kapa Agent{' '}
      <Box component="span" sx={{ verticalAlign: 'middle', lineHeight: 1 }}>
        <IconSparkles size={20} stroke={1.5} />
      </Box>
    </>
  ),
  subtitle: 'What can I help with?',
  description: 'Explore analytics, troubleshoot issues, or get setup help.',
  examplePrompts: KAPA_EXAMPLE_PROMPTS,
  inputPlaceholder: 'e.g., Show me coverage gaps for last week',
  footerExtra: <KapaBetaSignupLink />,
};

interface KapaAgentProviderProps {
  children: React.ReactNode;
  /** Called after the agent created, updated or deployed a source. */
  onSourceChanged?: (sourceId: string, change: SourceChange) => void;
}

export const KapaAgentProvider = ({
  children,
  onSourceChanged,
}: KapaAgentProviderProps) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const projectId = auth.project.selected?.id ?? '';
  const canEditSources =
    auth.project.selected?.permissions.all.includes('edit_sources') ?? false;

  const [highlightedSourceIds, setHighlightedSourceIds] = useState<string[]>(
    [],
  );
  const clearSourceHighlight = useCallback((sourceId: string) => {
    setHighlightedSourceIds((ids) => ids.filter((id) => id !== sourceId));
  }, []);
  const sourceHighlights = useMemo(
    () => ({ highlightedSourceIds, clearSourceHighlight }),
    [highlightedSourceIds, clearSourceHighlight],
  );

  const tools = useMemo(
    () =>
      createAgentTools({
        onNavigate: (path) => navigate(path),
        canEditSources,
        onSourceChanged: (sourceId, change) => {
          if (change !== 'updated') {
            setHighlightedSourceIds((ids) =>
              ids.includes(sourceId) ? ids : [...ids, sourceId],
            );
          }
          onSourceChanged?.(sourceId, change);
        },
      }),
    [navigate, canEditSources, onSourceChanged],
  );

  const userId = auth.user?.user.id ?? '';
  const toolContext = useMemo<ToolContext>(
    () => ({ authFetch: auth.authFetch, projectId, userId }),
    [auth.authFetch, projectId, userId],
  );

  const getSessionToken = useCallback(async () => {
    const res = await auth.authFetch(
      `/agent/v1/internal/projects/${projectId}/agent/sessions/`,
      { method: 'POST' },
    );

    if (!res.ok) {
      throw new Error(`Failed to create agent session: ${res.status}`);
    }

    const data = (await res.json()) as {
      session_token: string;
      expires_at: string;
    };

    return {
      token: data.session_token,
      expiresAt: new Date(data.expires_at).getTime(),
    };
  }, [auth, projectId]);

  const authUser = auth.user?.user;
  const user = useMemo(
    () =>
      authUser
        ? {
            email: authUser.email,
          }
        : undefined,
    [authUser],
  );

  const handleEvent = useCallback<OnAgentEvent>(
    (event) => {
      posthog?.capture(`agent_${event.type}`, {
        ...event.data,
        project_id: auth.project.selected?.id,
        project_name: auth.project.selected?.project_name,
      });
    },
    [auth.project.selected?.id, auth.project.selected?.project_name],
  );

  return (
    <AgentProvider
      getSessionToken={getSessionToken}
      projectId={AGENT_PROJECT_ID}
      tools={tools}
      context={toolContext}
      builtinToolMeta={BUILTIN_TOOL_META}
      customInstructions={AGENT_CUSTOM_INSTRUCTIONS}
      model={AGENT_MODEL}
      user={user}
      integrationId={AGENT_INTEGRATION_ID}
      onEvent={handleEvent}
      // Matches the dashboard primary. Relies on the SDK's luminance-based
      // accentForeground so the send and approval controls keep a readable dark
      // label on the light accent surface.
      theme={{ accentColor: PRIMARY_COLOR }}
      apiBaseUrl={AGENT_API_BASE_URL}
      enableHistory={true}
    >
      <AgentUIProvider onEvent={handleEvent}>
        <ProjectResetHandler projectId={projectId} />
        <SourceHighlightsContext value={sourceHighlights}>
          {children}
        </SourceHighlightsContext>
      </AgentUIProvider>
    </AgentProvider>
  );
};
